import React, { useState, useContext, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import { Tree, TreeNode } from 'react-organizational-chart';
import { TranslatorContext } from '../../context/Translator';
import PageLayout from '../../layouts/PageLayout';
import { organizationStructure } from '../../APIs/UserRequests';

// CSS styles for the organization chart
const nodeStyles = {
  styledNode: {
    padding: '0',
    borderRadius: '8px',
    display: 'inline-block',
    border: 'none',
    backgroundColor: '#fff',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    textAlign: 'center',
    minWidth: '200px',
    maxWidth: '220px',
    margin: '8px',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    overflow: 'hidden',
    position: 'relative'
  },
  nodeImageContainer: {
    backgroundColor: '#fff',
    padding: '20px 15px 15px 15px',
    position: 'relative'
  },
  nodeImage: {
    width: '70px',
    height: '70px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '4px solid #17a2b8',
    marginBottom: '10px',
    display: 'block',
    margin: '0 auto'
  },
  nodeContent: {
    backgroundColor: '#fff',
    padding: '0 15px 20px 15px',
    color: '#333'
  },
  nodeTitle: {
    fontWeight: 'bold',
    fontSize: '16px',
    color: '#333',
    marginBottom: '6px',
    lineHeight: '1.2'
  },
  nodeSubtitle: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '2px',
    lineHeight: '1.2'
  },
  nodeEmail: {
    fontSize: '11px',
    color: '#888',
    opacity: 0.9
  }
};

// Component to render individual nodes
const OrganizationNode = ({ node, onToggleCollapse, isCollapsed }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  const hoverStyle = isHovered ? {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.25)'
  } : {};

  const handleImageError = () => {
    setImageError(true);
  };

  const handleToggleClick = (e) => {
    e.stopPropagation();
    if (onToggleCollapse) {
      onToggleCollapse(node.id);
    }
  };

  const defaultImage = "/images/default.png"; // Default image path
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div 
      style={{...nodeStyles.styledNode, ...hoverStyle}}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header section with image */}
      <div style={nodeStyles.nodeImageContainer}>
        {node.image && !imageError ? (
          <img 
            src={node.image} 
            alt={node.name}
            style={nodeStyles.nodeImage}
            onError={handleImageError}
          />
        ) : !imageError && defaultImage ? (
          <img 
            src={defaultImage} 
            alt={node.name}
            style={nodeStyles.nodeImage}
            onError={handleImageError}
          />
        ) : (
          <div 
            style={{
              ...nodeStyles.nodeImage,
              backgroundColor: '#17a2b8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: '28px',
              fontWeight: 'bold',
              border: '4px solid #17a2b8'
            }}
          >
            {node.name.charAt(0).toUpperCase()}
          </div>
        )}
      </div>
      
      {/* Content section with text */}
      <div style={nodeStyles.nodeContent}>
        <div style={nodeStyles.nodeTitle}>{node.name}</div>
        {node.user_type && <div style={nodeStyles.nodeEmail}>{node.user_type}</div>}
        {hasChildren && (
          <div style={{
            fontSize: '10px',
            color: '#999',
            marginTop: '4px',
            fontStyle: 'italic'
          }}>
            {isCollapsed ? `${node.children.length} members hidden` : `${node.children.length} direct reports`}
          </div>
        )}
      </div>

      {/* Collapse/Expand button underneath the card */}
      {hasChildren && (
        <div 
          style={{
            backgroundColor: '#f8f9fa',
            borderTop: '1px solid #e9ecef',
            padding: '8px',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontSize: '12px',
            color: '#17a2b8',
            fontWeight: '500'
          }}
          onClick={handleToggleClick}
          title={isCollapsed ? 'Expand to show subordinates' : 'Collapse to hide subordinates'}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#e9ecef';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#f8f9fa';
          }}
        >
          <i className="material-icons" style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: '4px' }}>
            {isCollapsed ? 'expand_more' : 'expand_less'}
          </i>
          {isCollapsed ? 'Show Subordinates' : 'Hide Subordinates'}
        </div>
      )}
    </div>
  );
};

// Recursive component to render the tree structure
const OrganizationTree = ({ data, collapsedNodes, onToggleCollapse }) => {
  if (!data) return null;

  // Handle the nested data structure
  const nodeData = data.data || data;
  const isCollapsed = collapsedNodes.has(nodeData.id);

  return (
    <TreeNode 
      label={
        <OrganizationNode 
          node={nodeData} 
          onToggleCollapse={onToggleCollapse}
          isCollapsed={isCollapsed}
        />
      }
    >
      {!isCollapsed && nodeData.children && nodeData.children.map((child) => (
        <OrganizationTree 
          key={child.id || child.data?.id} 
          data={child} 
          collapsedNodes={collapsedNodes}
          onToggleCollapse={onToggleCollapse}
        />
      ))}
    </TreeNode>
  );
};

export default function OrganizationChartPage() {
  const { t } = useContext(TranslatorContext);
  const [organizationCharts, setOrganizationCharts] = useState([]);
  const [selectedChart, setSelectedChart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsedNodes, setCollapsedNodes] = useState(new Set());

  // Fetch organization data on component mount
  useEffect(() => {
    const loadOrganizationData = () => {
      setLoading(true);
      organizationStructure().then(res => {
        const charts = res.data.data;
        setOrganizationCharts(charts);
        if (charts && charts.length > 0) {
          setSelectedChart(charts[0]);
        }
      }).catch(err => {
        setError('Failed to load organization data');
        console.error('Error loading organization data:', err);
      }).finally(() => {
        setLoading(false);
      });
    };
    
    loadOrganizationData();
  }, []);
  
  const handleChartChange = (chartId) => {
    const chart = organizationCharts.find(chart => chart.id === parseInt(chartId));
    if (chart) {
      setSelectedChart(chart);
      // Reset collapsed nodes when changing charts
      setCollapsedNodes(new Set());
    }
  };

  const handleToggleCollapse = (nodeId) => {
    setCollapsedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setCollapsedNodes(new Set());
  };

  const collapseAll = () => {
    const allNodeIds = new Set();
    
    const collectNodeIds = (node) => {
      const nodeData = node.data || node;
      if (nodeData.children && nodeData.children.length > 0) {
        allNodeIds.add(nodeData.id);
        nodeData.children.forEach(collectNodeIds);
      }
    };
    
    if (selectedChart) {
      collectNodeIds(selectedChart);
    }
    
    setCollapsedNodes(allNodeIds);
  };

  // Show loading state
  if (loading) {
    return (
      <PageLayout>
        <Row>
          <Col xl={12}>
            <div className="mc-card">
              <div className="mc-card-body text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">{t('loading_organization_data') || 'Loading organization data...'}</p>
              </div>
            </div>
          </Col>
        </Row>
      </PageLayout>
    );
  }

  // Show error state
  if (error) {
    return (
      <PageLayout>
        <Row>
          <Col xl={12}>
            <div className="mc-card">
              <div className="mc-card-body text-center">
                <div className="alert alert-danger" role="alert">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {error}
                </div>
                <button 
                  className="btn btn-primary"
                  onClick={() => window.location.reload()}
                >
                  {t('retry') || 'Retry'}
                </button>
              </div>
            </div>
          </Col>
        </Row>
      </PageLayout>
    );
  }


  if (!organizationCharts.length || !selectedChart) {
    return (
      <PageLayout>
        <Row>
          <Col xl={12}>
            <div className="mc-card">
              <div className="mc-card-body text-center">
                <i className="fas fa-sitemap fa-3x text-muted mb-3"></i>
                <h5>{t('no_organization_data') || 'No organization data available'}</h5>
                <p className="text-muted">{t('no_organization_description') || 'No organizational structure has been configured yet.'}</p>
              </div>
            </div>
          </Col>
        </Row>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Row>
        <Col xl={12}>
          <div className="mc-card">
            <div className='mc-breadcrumb'>
              <h3 className="mc-breadcrumb-title">{t('organization_chart') || 'Organization Chart'}</h3>
            </div>
          </div>
        </Col>
        
        {/* Chart Selection */}
        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-card-header">
              <h4 className="mc-card-title">{t('select_chart') || 'Select Organization Chart'}</h4>
            </div>
            <div className="mc-card-body">
              <Row>
                <Col md={6}>
                  <div className="form-group">
                    <label htmlFor="chartSelect" className="form-label">
                      {t('organizational_unit') || 'Organizational Unit'}:
                    </label>
                    <select 
                      id="chartSelect"
                      className="form-select"
                      value={selectedChart.id}
                      onChange={(e) => handleChartChange(e.target.value)}
                    >
                      {organizationCharts.map(chart => (
                        <option key={chart.id} value={chart.id}>
                          {chart.data.name} {chart.data.title ? `- ${chart.data.title}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </Col>
                
              </Row>
            </div>
          </div>
        </Col>
        
        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-card-header">
              <h4 className="mc-card-title">
                {selectedChart.data.name} {selectedChart.data.title ? `- ${selectedChart.data.title}` : ''}
                <small className="text-muted ms-2">{selectedChart.data.role ? `- ${selectedChart.data.role}` : ''}</small>
              </h4>
            </div>
            
            <div className="mc-card-body" style={{ 
              overflowX: 'auto', 
              padding: '30px',
              background: '#f8f9fa',
              minHeight: '500px'
            }}>
              <Tree
                lineWidth="2px"
                lineColor="#17a2b8"
                lineBorderRadius="4px"
                label={
                  <OrganizationNode 
                    node={selectedChart.data} 
                    onToggleCollapse={handleToggleCollapse}
                    isCollapsed={collapsedNodes.has(selectedChart.data.id)}
                  />
                }
              >
                {!collapsedNodes.has(selectedChart.data.id) && selectedChart.data.children && selectedChart.data.children.map((child) => (
                  <OrganizationTree 
                    key={child.id} 
                    data={child} 
                    collapsedNodes={collapsedNodes}
                    onToggleCollapse={handleToggleCollapse}
                  />
                ))}
              </Tree>
            </div>
          </div>
        </Col>

        {/* Chart Statistics */}
        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-card-header">
              <h4 className="mc-card-title">{t('chart_statistics') || 'Chart Statistics'}</h4>
            </div>
            <div className="mc-card-body">
              <Row>
                <Col md={3}>
                  <div className="stat-item text-center">
                    <h5 className="text-primary">{organizationCharts.length}</h5>
                    <small className="text-muted">{t('total_charts') || 'Total Charts'}</small>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="stat-item text-center">
                    <h5 className="text-success">{getNodeCount(selectedChart.data)}</h5>
                    <small className="text-muted">{t('employees_in_chart') || 'Employees in Chart'}</small>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="stat-item text-center">
                    <h5 className="text-info">{getDepthLevel(selectedChart.data)}</h5>
                    <small className="text-muted">{t('hierarchy_levels') || 'Hierarchy Levels'}</small>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </Col>
      </Row>
    </PageLayout>
  );
}

// Helper functions for statistics
function getNodeCount(node) {
  if (!node) return 0;
  
  // Handle nested data structure
  const nodeData = node.data || node;
  let count = 1;
  
  if (nodeData.children) {
    nodeData.children.forEach(child => {
      count += getNodeCount(child);
    });
  }
  return count;
}

function getDepthLevel(node, currentLevel = 1) {
  if (!node) return currentLevel;
  
  // Handle nested data structure
  const nodeData = node.data || node;
  
  if (!nodeData.children || nodeData.children.length === 0) {
    return currentLevel;
  }
  
  let maxDepth = currentLevel;
  nodeData.children.forEach(child => {
    const childDepth = getDepthLevel(child, currentLevel + 1);
    maxDepth = Math.max(maxDepth, childDepth);
  });
  
  return maxDepth;
}
